import React from 'react';
import { X, Calendar, User, GitBranch, Hash, MessageSquare } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const VersionDetailsModal = ({ version, onClose }) => {
  if (!version) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Version Details - v{version.version_number}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Version Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <Hash className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Version Number</div>
                <div className="text-sm text-gray-600">v{version.version_number}</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className={`h-3 w-3 rounded-full ${version.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
              <div>
                <div className="text-sm font-medium text-gray-900">Status</div>
                <div className="text-sm text-gray-600">
                  {version.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <User className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Author</div>
                <div className="text-sm text-gray-600">{version.author_name || 'Unknown'}</div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Created</div>
                <div className="text-sm text-gray-600">
                  {format(new Date(version.created_at), 'MMM d, yyyy')}
                </div>
                <div className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}
                </div>
              </div>
            </div>
          </div>

          {/* Change Message */}
          {version.change_message && (
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <MessageSquare className="h-5 w-5 text-gray-400" />
                <div className="text-sm font-medium text-gray-900">Change Message</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                {version.change_message}
              </div>
            </div>
          )}

          {/* Content Preview */}
          <div>
            <div className="text-sm font-medium text-gray-900 mb-2">Content Preview</div>
            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                {version.content}
              </pre>
            </div>
          </div>

          {/* Metadata */}
          <div className="border-t border-gray-200 pt-4">
            <div className="text-sm font-medium text-gray-900 mb-2">Metadata</div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Version ID:</span>
                <span className="ml-2 text-gray-600 font-mono text-xs">{version.id}</span>
              </div>
              {version.updated_at && version.updated_at !== version.created_at && (
                <div>
                  <span className="font-medium text-gray-700">Last Updated:</span>
                  <span className="ml-2 text-gray-600">
                    {format(new Date(version.updated_at), 'MMM d, yyyy HH:mm')}
                  </span>
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Content Length:</span>
                <span className="ml-2 text-gray-600">{version.content.length} characters</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Lines:</span>
                <span className="ml-2 text-gray-600">{version.content.split('\n').length} lines</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionDetailsModal; 